import { supabase } from '../config/supabase';
import { refreshTripRouteEtaById } from '../services/tripRouteEta';
import { toGeoPointWKT } from './formatters';
import { fetchMapSettings } from '../maps/settings';
import { geocodeAddressClient } from '../maps/clientGeocode';
import { isGeocodeMockSydneyEnabled, mockSydneyGeocode } from '../maps/mockSydneyGeocode';
import { invokeEdgeFunction } from '../observability/invokeEdgeFunction';
import type { GeocodeResult } from '../types/database';

export type GeocodeField = 'pickup' | 'dropoff';

export interface AddressInput {
  address: string;
  lat?: number | null;
  lng?: number | null;
}

export interface TripGeopointsResult {
  pickup_address: string;
  dropoff_address: string;
  pickup_geopoint: string;
  dropoff_geopoint: string;
}

export interface GeocodeFieldFailure {
  field: GeocodeField;
  message: string;
}

export class TripGeocodeError extends Error {
  readonly failures: GeocodeFieldFailure[];

  constructor(failures: GeocodeFieldFailure[]) {
    const summary = failures.map((f) => f.message).join(' ');
    super(summary || 'Could not resolve address coordinates');
    this.name = 'TripGeocodeError';
    this.failures = failures;
  }
}

/** Parse geocode edge function response (supports { results[] } and direct shape). */
export function parseGeocodeResponse(data: unknown): GeocodeResult | null {
  if (!data || typeof data !== 'object' || 'error' in data) {
    return null;
  }

  const payload = data as GeocodeResult & { results?: GeocodeResult[] };

  if (Array.isArray(payload.results) && payload.results.length > 0) {
    const first = payload.results[0];
    if (typeof first.lat === 'number' && typeof first.lng === 'number') {
      return first;
    }
  }

  if (typeof payload.lat === 'number' && typeof payload.lng === 'number') {
    return payload;
  }

  return null;
}

function coordinatesToResult(input: AddressInput): GeocodeResult | null {
  if (typeof input.lat === 'number' && typeof input.lng === 'number') {
    return {
      lat: input.lat,
      lng: input.lng,
      formatted_address: input.address.trim(),
    };
  }
  return null;
}

export async function geocodeAddress(address: string): Promise<GeocodeResult | null> {
  const trimmed = address?.trim();
  if (!trimmed) return null;

  // TEMPORARY: Google geocode unavailable — random Sydney coords (see mockSydneyGeocode).
  if (isGeocodeMockSydneyEnabled()) {
    return mockSydneyGeocode(trimmed);
  }

  // Browser: use Google Maps SDK directly when available.
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    try {
      const settings = await fetchMapSettings();
      const clientResult = await geocodeAddressClient(trimmed, settings);
      if (clientResult) return clientResult;
    } catch {
      // Fall through to edge function when client SDK fails.
    }
  }

  const { data, error } = await invokeEdgeFunction('geocode-address', {
    body: { address: trimmed },
    reportError: false,
  });

  if (error) {
    return null;
  }

  return parseGeocodeResponse(data);
}

async function resolveAddressInput(
  field: GeocodeField,
  input: AddressInput
): Promise<{ result: GeocodeResult | null; failure: GeocodeFieldFailure | null }> {
  const trimmed = input.address?.trim();
  if (!trimmed) {
    return {
      result: null,
      failure: {
        field,
        message: `${field === 'pickup' ? 'Pickup' : 'Drop-off'} address is required.`,
      },
    };
  }

  const fromCoords = coordinatesToResult({ ...input, address: trimmed });
  if (fromCoords) {
    return { result: fromCoords, failure: null };
  }

  const geocoded = await geocodeAddress(trimmed);
  if (geocoded) {
    return { result: geocoded, failure: null };
  }

  return {
    result: null,
    failure: {
      field,
      message: `Could not locate ${field === 'pickup' ? 'pickup' : 'drop-off'} address. Select a suggestion or enter a full street address.`,
    },
  };
}

/** Resolve both trip endpoints. Missing coordinates return null geopoints (legacy). */
export async function resolveTripGeopoints(
  pickupAddress: string,
  dropoffAddress: string
): Promise<{
  pickup_address: string;
  dropoff_address: string;
  pickup_geopoint: string | null;
  dropoff_geopoint: string | null;
}> {
  const [pickup, dropoff] = await Promise.all([
    resolveAddressInput('pickup', { address: pickupAddress }),
    resolveAddressInput('dropoff', { address: dropoffAddress }),
  ]);

  return {
    pickup_address: pickup.result?.formatted_address ?? pickupAddress,
    dropoff_address: dropoff.result?.formatted_address ?? dropoffAddress,
    pickup_geopoint: pickup.result ? toGeoPointWKT(pickup.result.lat, pickup.result.lng) : null,
    dropoff_geopoint: dropoff.result ? toGeoPointWKT(dropoff.result.lat, dropoff.result.lng) : null,
  };
}

/** Resolve trip endpoints and throw if either address cannot be geocoded. */
export async function resolveTripGeopointsStrict(
  pickup: AddressInput,
  dropoff: AddressInput
): Promise<TripGeopointsResult> {
  const [pickupResolved, dropoffResolved] = await Promise.all([
    resolveAddressInput('pickup', pickup),
    resolveAddressInput('dropoff', dropoff),
  ]);

  const failures = [pickupResolved.failure, dropoffResolved.failure].filter(
    (item): item is GeocodeFieldFailure => item !== null
  );

  if (failures.length > 0 || !pickupResolved.result || !dropoffResolved.result) {
    throw new TripGeocodeError(failures);
  }

  return {
    pickup_address: pickupResolved.result.formatted_address,
    dropoff_address: dropoffResolved.result.formatted_address,
    pickup_geopoint: toGeoPointWKT(pickupResolved.result.lat, pickupResolved.result.lng),
    dropoff_geopoint: toGeoPointWKT(dropoffResolved.result.lat, dropoffResolved.result.lng),
  };
}

/** Backfill geopoints for a trip row missing coordinates. Returns true when updated. */
export async function backfillTripGeopoints(trip: {
  id: string;
  pickup_address: string;
  dropoff_address: string;
  pickup_geopoint?: string | null;
  dropoff_geopoint?: string | null;
}): Promise<boolean> {
  const pickupNeeds = !trip.pickup_geopoint && !!trip.pickup_address?.trim();
  const dropoffNeeds = !trip.dropoff_geopoint && !!trip.dropoff_address?.trim();
  if (!pickupNeeds && !dropoffNeeds) return false;

  const geopoints = await resolveTripGeopointsStrict(
    { address: trip.pickup_address },
    { address: trip.dropoff_address }
  );

  const { error } = await supabase
    .from('trips')
    .update({
      pickup_address: geopoints.pickup_address,
      dropoff_address: geopoints.dropoff_address,
      pickup_geopoint: geopoints.pickup_geopoint,
      dropoff_geopoint: geopoints.dropoff_geopoint,
      updated_at: new Date().toISOString(),
    })
    .eq('id', trip.id);

  if (error) throw error;

  try {
    await refreshTripRouteEtaById(trip.id);
  } catch (etaErr) {
    console.warn('refreshTripRouteEtaById after backfill failed:', etaErr);
  }

  return true;
}
